<template>
  <div class="dispatch-board">
    <!-- Add Engineer Bar -->
    <div v-if="!readonly" class="flex items-end gap-3 mb-4 pb-4 border-b">
      <div style="width: 250px;">
        <label class="block uppercase font-semibold text-sm mb-1">Add Engineer</label>
        <StaffAutocomplete class="w-full" v-model="selectedEngineer" :forceSelection="true" placeholder="Search staff..." />
      </div>
      <div>
        <button
            type="button"
            @click="addEngineer"
            :disabled="!selectedEngineer || !hasDateRange"
            class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg class="stroke-current h-4 w-4 mr-1" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          <span>Add to All Days</span>
        </button>
      </div>
      <div v-if="!hasDateRange" class="text-sm text-red-400 italic pb-2">
        Set Start Date and End Date first
      </div>
      <div v-if="isCheckingClashes" class="text-sm text-gray-400 italic pb-2">
        Checking clashes…
      </div>
    </div>

    <!-- Dispatch Grid -->
    <div v-if="dateColumns.length > 0 && engineerList.length > 0">
      <!-- Default Time Header -->
      <div v-if="defaultStartTime || defaultEndTime" class="flex items-center mb-2 text-xs text-gray-400">
        <span class="mr-3">Default time:</span>
        <span v-if="defaultStartTime" class="bg-gray-100 px-2 py-1 rounded mr-2">{{ defaultStartTime }}</span>
        <span v-if="defaultStartTime && defaultEndTime"> — </span>
        <span v-if="defaultEndTime" class="bg-gray-100 px-2 py-1 rounded ml-2">{{ defaultEndTime }}</span>
      </div>

      <div class="dispatch-scroll relative">
        <div v-if="isCheckingClashes" class="dispatch-progress-bar" aria-hidden="true"></div>
        <table class="dispatch-tbl">
          <thead>
            <tr>
              <th class="dt-sticky-left dt-cell dt-header-cell">
                <span class="uppercase text-xs font-semibold text-gray-500">Engineer</span>
              </th>
              <th
                  v-for="col in dateColumns"
                  :key="'h-' + col.epoch"
                  class="dt-cell dt-header-cell dt-date-header"
                  :class="{ 'header-today': col.isToday && !col.isWeekend, 'header-weekend': col.isWeekend }"
              >
                <div class="font-semibold text-xs">{{ col.dayName }}</div>
                <div class="text-xs text-gray-400">{{ col.dayDate }}</div>
              </th>
              <th v-if="!readonly" class="dt-sticky-right dt-cell dt-header-cell" style="width:44px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="eng in engineerList" :key="eng.engineer_id">
              <td class="dt-sticky-left dt-cell dt-eng-cell" :class="{ 'eng-cell-inactive': eng.assignedCount === 0 }">
                <div class="font-semibold text-sm text-blue-700 hover:text-blue-900 cursor-pointer underline-offset-2 hover:underline truncate" @click="openAvailabilityPanel(eng)" :title="eng.name">{{ eng.name }}</div>
                <div v-if="eng.assignedCount === 0" class="text-xs text-gray-400 italic">No days selected</div>
                <div v-else class="text-xs text-gray-400">{{ eng.assignedCount }}/{{ dateColumns.length }} days</div>
              </td>
              <td
                  v-for="col in dateColumns"
                  :key="col.epoch"
                  class="dt-cell dt-date-cell"
                  :class="getCellBgClass(eng.engineer_id, col)"
              >
                <div v-if="isAssigned(eng.engineer_id, col.epoch)" class="assigned-cell relative cursor-pointer" :class="getCellStatusClass(eng.engineer_id, col.epoch)" @click="toggleAssignment(eng.engineer_id, col.epoch)" :title="getCellTooltip(eng.engineer_id, col.epoch)">
                  <div v-if="getCellWarning(eng.engineer_id, col.epoch)" class="corner-badge-left" :title="getCellWarning(eng.engineer_id, col.epoch).message"><svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg></div>
                  <button v-if="!readonly && !isMarkedForRemoval(eng.engineer_id, col.epoch)" type="button" class="corner-badge-right time-edit-btn" @click.stop="openTimeEditor(eng.engineer_id, col.epoch, $event)" title="Set time" aria-label="Set time"><svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg></button>
                  <div v-if="isMarkedForRemoval(eng.engineer_id, col.epoch)" class="removed-overlay"><svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
                  <template v-else>
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </template>
                </div>
                <div v-else-if="getCellWarning(eng.engineer_id, col.epoch)" class="empty-cell relative" :class="{ 'cursor-pointer': !readonly }" @click="!readonly && toggleAssignment(eng.engineer_id, col.epoch)" :title="getCellWarning(eng.engineer_id, col.epoch).message">
                  <div class="warning-badge-empty"><svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg></div>
                </div>
                <div v-else-if="!readonly" class="empty-cell cursor-pointer" @click="toggleAssignment(eng.engineer_id, col.epoch)" title="Click to assign">
                  <svg class="h-4 w-4 mx-auto text-gray-300" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </div>
                <div v-else class="p-3"></div>
              </td>
              <td v-if="!readonly" class="dt-sticky-right dt-cell dt-del-cell">
                <button type="button" @click="confirmRemoveEngineer(eng.engineer_id)" class="text-red-400 hover:text-red-600 transition-colors p-1" title="Remove engineer">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Legend — outside scroll container -->
      <div class="flex items-center gap-4 mt-3 px-1 text-xs text-gray-400">
        <div class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded bg-green-100 border border-green-300"></span> Assigned</div>
        <div class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded bg-yellow-200"></span> In Progress</div>
        <div class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded bg-emerald-300"></span> Completed</div>
        <div class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background: repeating-linear-gradient(45deg, #fee2e2, #fee2e2 2px, #fff 2px, #fff 4px);"></span> On Leave</div>
        <div class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded border-2 border-orange-400 bg-orange-50"></span> Other Schedule</div>
        <div class="flex items-center gap-1">
          <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          Custom time
        </div>
      </div>

      <!-- Summary bar — outside scroll container -->
      <div class="flex items-center justify-between mt-2 px-1">
        <div class="text-xs text-gray-400">
          {{ engineerList.length }} engineer{{ engineerList.length !== 1 ? 's' : '' }} &middot;
          {{ activeAssignmentCount }} assignment{{ activeAssignmentCount !== 1 ? 's' : '' }} &middot;
          {{ dateColumns.length }} day{{ dateColumns.length !== 1 ? 's' : '' }}
          <span v-if="removedAssignments.length" class="text-red-500 ml-2">({{ removedAssignments.length }} to be removed)</span>
        </div>
        <div v-if="!readonly" class="flex gap-2">
          <button type="button" @click="fillAll" class="text-xs text-blue-600 hover:text-blue-800 underline">Fill all</button>
          <button type="button" @click="clearAll" class="text-xs text-red-500 hover:text-red-700 underline">Clear all</button>
        </div>
      </div>
    </div>

    <!-- Empty states -->
    <div v-else-if="dateColumns.length > 0 && engineerList.length === 0" class="text-gray-400 text-center py-6 text-sm">
      No engineers assigned. Use the search above to add engineers.
    </div>
    <div v-else class="text-gray-400 text-center py-6 text-sm">
      Set schedule Start Date and End Date to see the dispatch board.
    </div>

    <!-- Time Editor Popover -->
    <div v-if="timeEditor.visible" class="fixed inset-0 z-50" @click="closeTimeEditor">
      <div
          class="absolute bg-white shadow-xl rounded-lg border border-gray-200 p-3"
          :style="{ top: timeEditor.y + 'px', left: timeEditor.x + 'px' }"
          @click.stop
          style="width: 220px;"
      >
        <div class="flex items-center justify-between mb-3">
          <span class="text-xs font-semibold text-gray-500 uppercase">Set Time</span>
          <button type="button" @click="closeTimeEditor" class="text-gray-400 hover:text-gray-600">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div class="mb-2">
          <label class="block text-xs font-semibold text-gray-500 mb-1">From</label>
          <Calendar
              v-model="timeEditor.startTimeDate"
              :timeOnly="true"
              class="w-full"
          />
        </div>
        <div class="mb-3">
          <label class="block text-xs font-semibold text-gray-500 mb-1">To</label>
          <Calendar
              v-model="timeEditor.endTimeDate"
              :timeOnly="true"
              class="w-full"
          />
        </div>

        <div class="flex items-center justify-between">
          <button
              v-if="timeEditor.startTimeDate || timeEditor.endTimeDate"
              type="button"
              @click="clearCellTime"
              class="text-xs text-red-500 hover:text-red-700 underline"
          >
            Use default
          </button>
          <div v-else></div>
          <button
              type="button"
              @click="saveCellTime"
              :disabled="timeEditor.isChecking"
              class="px-3 py-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
          >
            {{ timeEditor.isChecking ? 'Checking…' : 'Apply' }}
          </button>
        </div>
        <p
            v-if="timeEditor.errorMessage"
            class="mt-2 text-xs text-red-600 leading-tight"
        >
          {{ timeEditor.errorMessage }}
        </p>
      </div>
    </div>

    <!-- Engineer Availability Panel -->
    <portal to="overlay-outlet">
      <panel
          :showing="availabilityPanel.visible"
          @close="availabilityPanel.visible = false"
          :title="availabilityPanel.engineerName ? availabilityPanel.engineerName + ' — Availability' : 'Engineer Availability'"
      >
        <EngineerAvailabilityPanel
            v-if="availabilityPanel.visible && availabilityPanel.engineerId"
            :engineerId="availabilityPanel.engineerId"
            :engineerName="availabilityPanel.engineerName"
            :engineerEmail="availabilityPanel.engineerEmail"
            :scheduleId="scheduleId"
            :scheduleStartDate="startDate"
            :scheduleEndDate="endDate"
        />
      </panel>
    </portal>
  </div>
</template>

<script>
import ScheduleService from "@/services/scheduleService";
import StaffAutocomplete from "@/components/AutoComplete/StaffAutoComplete.vue";
import Calendar from "primevue/calendar";
import Panel from "@/components/Panel.vue";
import EngineerAvailabilityPanel from "./EngineerAvailabilityPanel.vue";
import notificationMixin from "@/mixins/notificationMixin";
import {
  dateToUtcMidnightEpoch,
  epochToYyyyMmDd,
  isDayInRange,
} from "@/utils/scheduleDate";

export default {
  name: "DispatchBoard",
  mixins: [notificationMixin],
  components: { StaffAutocomplete, Calendar, Panel, EngineerAvailabilityPanel },
  props: {
    value: { type: Array, default: () => [] },
    startDate: { type: [Date, Number], default: null },
    endDate: { type: [Date, Number], default: null },
    defaultStartTime: { type: String, default: null },
    defaultEndTime: { type: String, default: null },
    readonly: { type: Boolean, default: false },
    scheduleId: { type: String, default: null },
  },
  data() {
    return {
      selectedEngineer: null,
      removedAssignments: [],
      warningMap: {},
      isCheckingClashes: false,
      availabilityPanel: {
        visible: false,
        engineerId: null,
        engineerName: '',
        engineerEmail: '',
      },
      timeEditor: {
        visible: false,
        engineerId: null,
        dateEpoch: null,
        startTimeDate: null,
        endTimeDate: null,
        x: 0,
        y: 0,
        errorMessage: null,
        isChecking: false,
      },
    };
  },
  computed: {
    hasDateRange() {
      return this.startDateEpoch && this.endDateEpoch && this.endDateEpoch >= this.startDateEpoch;
    },
    startDateEpoch() {
      if (this.startDate == null) return null;
      if (typeof this.startDate === 'number') return this.startDate;
      return dateToUtcMidnightEpoch(this.startDate);
    },
    endDateEpoch() {
      if (this.endDate == null) return null;
      if (typeof this.endDate === 'number') return this.endDate;
      return dateToUtcMidnightEpoch(this.endDate);
    },
    dateColumns() {
      if (!this.hasDateRange) return [];
      const cols = [];
      const today = this.$moment().startOf("day").unix();
      let current = this.startDateEpoch;
      while (current <= this.endDateEpoch) {
        const m = this.$moment.unix(current);
        const dayOfWeek = m.day();
        cols.push({
          epoch: current,
          dayName: m.format("ddd"),
          dayDate: m.format("DD MMM"),
          isToday: current === today,
          isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
        });
        current += 86400;
      }
      return cols;
    },
    engineerList() {
      const map = {};
      this.value.forEach(a => {
        if (this.isMarkedForRemoval(a.engineer_id, a.scheduled_date)) return;
        if (!map[a.engineer_id]) {
          map[a.engineer_id] = { engineer_id: a.engineer_id, name: a.engineer_name || a.engineer_id, email: a.engineer_email || '', assignedCount: 0 };
        }
        map[a.engineer_id].assignedCount++;
      });
      this.value.forEach(a => {
        if (!map[a.engineer_id]) {
          map[a.engineer_id] = { engineer_id: a.engineer_id, name: a.engineer_name || a.engineer_id, email: a.engineer_email || '', assignedCount: 0 };
        }
      });
      return Object.values(map);
    },
    assignmentMap() {
      const m = {};
      this.value.forEach(a => { m[`${a.engineer_id}_${a.scheduled_date}`] = a; });
      return m;
    },
    activeAssignmentCount() {
      return this.value.length - this.removedAssignments.length;
    },
  },
  watch: {
    engineerList: { handler() { this.checkAllClashes(); }, deep: true },
    dateColumns: {
      handler(newCols, oldCols) {
        this.dropUnsavedOutsideRange();
        this.fillEngineersOnAddedDays(newCols, oldCols);
        this.checkAllClashes();
      },
    },
  },
  methods: {
    isAssigned(engineerId, dateEpoch) {
      return !!this.assignmentMap[`${engineerId}_${dateEpoch}`];
    },
    isMarkedForRemoval(engineerId, dateEpoch) {
      return this.removedAssignments.some(r => r.engineer_id === engineerId && r.scheduled_date === dateEpoch);
    },
    getCellStatusClass(engineerId, dateEpoch) {
      if (this.isMarkedForRemoval(engineerId, dateEpoch)) return 'cell-removed';
      const a = this.assignmentMap[`${engineerId}_${dateEpoch}`];
      if (!a) return '';
      switch (a.status) {
        case 'InProgress': return 'cell-inprogress';
        case 'Completed': return 'cell-completed';
        default: return 'cell-assigned';
      }
    },
    getCellBgClass(engineerId, col) {
      const warning = this.getCellWarning(engineerId, col.epoch);
      if (warning && warning.type === 'approved-leave') return 'cell-bg-leave';
      if (warning && warning.type === 'schedule-assignment') return 'cell-bg-clash';
      if (col.isWeekend && !this.isAssigned(engineerId, col.epoch)) return 'cell-bg-weekend';
      return '';
    },
    getCellWarning(engineerId, dateEpoch) {
      return this.warningMap[`${engineerId}_${dateEpoch}`] || null;
    },
    getCellTooltip(engineerId, dateEpoch) {
      const warning = this.getCellWarning(engineerId, dateEpoch);
      const a = this.assignmentMap[`${engineerId}_${dateEpoch}`];
      let tip = '';
      if (this.isMarkedForRemoval(engineerId, dateEpoch)) {
        tip = 'Click to restore';
      } else if (warning) {
        tip = warning.message;
      }
      if (a && a.start_time && a.end_time) {
        tip += (tip ? ' | ' : '') + a.start_time + ' — ' + a.end_time;
      } else if (a && a.start_time) {
        tip += (tip ? ' | ' : '') + 'From ' + a.start_time;
      } else if (a && a.end_time) {
        tip += (tip ? ' | ' : '') + 'Until ' + a.end_time;
      }
      return tip;
    },
    // ── Time Editor ──
    timeStringToDate(timeStr) {
      if (!timeStr) return null;
      const parts = timeStr.split(':');
      if (parts.length < 2) return null;
      const d = new Date();
      d.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
      return d;
    },
    dateToTimeString(dateObj) {
      if (!dateObj) return null;
      const h = String(dateObj.getHours()).padStart(2, '0');
      const m = String(dateObj.getMinutes()).padStart(2, '0');
      return `${h}:${m}`;
    },
    openTimeEditor(engineerId, dateEpoch, event) {
      const a = this.assignmentMap[`${engineerId}_${dateEpoch}`];
      if (!a) return;

      const rect = event.target.closest('td').getBoundingClientRect();
      const popoverWidth = 220;
      const popoverHeight = 220;

      let x = rect.left + rect.width / 2 - popoverWidth / 2;
      let y = rect.bottom + 4;

      if (x < 8) x = 8;
      if (x + popoverWidth > window.innerWidth - 8) x = window.innerWidth - popoverWidth - 8;
      if (y + popoverHeight > window.innerHeight - 8) y = rect.top - popoverHeight - 4;

      this.timeEditor = {
        visible: true,
        engineerId,
        dateEpoch,
        startTimeDate: this.timeStringToDate(a.start_time),
        endTimeDate: this.timeStringToDate(a.end_time),
        x,
        y,
        errorMessage: null,
        isChecking: false,
      };
    },
    closeTimeEditor() {
      this.timeEditor.visible = false;
      this.timeEditor.errorMessage = null;
      this.timeEditor.isChecking = false;
    },
    async saveCellTime() {
      const { engineerId, dateEpoch, startTimeDate, endTimeDate } = this.timeEditor;
      const startTime = this.dateToTimeString(startTimeDate);
      const endTime = this.dateToTimeString(endTimeDate);

      this.timeEditor.errorMessage = null;
      this.timeEditor.isChecking = true;
      try {
        const result = await ScheduleService.checkClashes({
          exclude_schedule_id: this.scheduleId,
          assignments: [{
            engineer_id: engineerId,
            scheduled_date: dateEpoch,
            start_time: startTime,
            end_time: endTime,
          }],
        });
        const clashes = (result && Array.isArray(result.clashes)) ? result.clashes : [];
        if (clashes.length > 0) {
          const c = clashes[0];
          let detail;
          if (c.type === 'invalid-time-range') detail = 'Start time must be earlier than end time.';
          else if (c.type === 'approved-leave') detail = 'Engineer is on approved leave.';
          else detail = c.message || 'Engineer already has an assignment on this date.';
          this.timeEditor.errorMessage = detail;
          this.timeEditor.isChecking = false;
          return;
        }
      } catch (err) {
        this.timeEditor.errorMessage = 'Could not check for clashes. Try again.';
        this.timeEditor.isChecking = false;
        return;
      }
      this.timeEditor.isChecking = false;

      const updated = this.value.map(a => {
        if (a.engineer_id === engineerId && a.scheduled_date === dateEpoch) {
          return { ...a, start_time: startTime, end_time: endTime };
        }
        return a;
      });
      this.$emit('input', updated);
      this.closeTimeEditor();
      this.$nextTick(() => this.checkAllClashes());
    },
    clearCellTime() {
      this.timeEditor.startTimeDate = null;
      this.timeEditor.endTimeDate = null;
      this.saveCellTime();
    },

    // ── Assignment management ──
    addEngineer() {
      if (!this.selectedEngineer || !this.hasDateRange) return;
      const engineerId = this.selectedEngineer.staff_id || this.selectedEngineer.id;
      const engineerName = this.selectedEngineer.name ||
          `${this.selectedEngineer.first_name || ''} ${this.selectedEngineer.last_name || ''}`.trim();
      const engineerEmail = this.selectedEngineer.email || '';
      if (this.engineerList.some(e => e.engineer_id === engineerId)) return;

      const newAssignments = [];
      let current = this.startDateEpoch;
      while (current <= this.endDateEpoch) {
        newAssignments.push({
          engineer_id: engineerId,
          engineer_name: engineerName,
          engineer_email: engineerEmail,
          scheduled_date: current,
          start_time: this.defaultStartTime || null,
          end_time: this.defaultEndTime || null,
          status: 'Assigned',
          assignment_id: null,
        });
        current += 86400;
      }
      this.$emit('input', [...this.value, ...newAssignments]);
      this.selectedEngineer = null;
    },

    removeEngineer(engineerId) {
      const engineerAssignments = this.value.filter(a => a.engineer_id === engineerId);
      const persisted = engineerAssignments.filter(a => a.assignment_id);
      const nonPersisted = engineerAssignments.filter(a => !a.assignment_id);
      persisted.forEach(a => {
        if (!this.isMarkedForRemoval(a.engineer_id, a.scheduled_date)) {
          this.removedAssignments.push({ engineer_id: a.engineer_id, scheduled_date: a.scheduled_date, assignment_id: a.assignment_id });
        }
      });
      if (nonPersisted.length > 0) {
        const keys = new Set(nonPersisted.map(a => `${a.engineer_id}_${a.scheduled_date}`));
        this.$emit('input', this.value.filter(a => !keys.has(`${a.engineer_id}_${a.scheduled_date}`)));
      }
    },

    toggleAssignment(engineerId, dateEpoch) {
      if (this.readonly) return;
      const existing = this.assignmentMap[`${engineerId}_${dateEpoch}`];
      if (existing) {
        // Toggle marked-for-removal state. Works the same way for persisted and
        // non-persisted entries, so the engineer row stays visible even when
        // every day is unselected — only the explicit "Remove engineer" button
        // can drop the engineer from the board.
        if (this.isMarkedForRemoval(engineerId, dateEpoch)) {
          this.removedAssignments = this.removedAssignments.filter(
            r => !(r.engineer_id === engineerId && r.scheduled_date === dateEpoch),
          );
        } else {
          this.removedAssignments.push({
            engineer_id: engineerId,
            scheduled_date: dateEpoch,
            assignment_id: existing.assignment_id || null,
          });
        }
      } else {
        const eng = this.engineerList.find(e => e.engineer_id === engineerId);
        this.$emit('input', [...this.value, {
          engineer_id: engineerId,
          engineer_name: eng ? eng.name : engineerId,
          scheduled_date: dateEpoch,
          start_time: this.defaultStartTime || null,
          end_time: this.defaultEndTime || null,
          status: 'Assigned',
          assignment_id: null,
        }]);
      }
    },

    async confirmRemoveEngineer(engineerId) {
      if (this.readonly) return;
      const eng = this.engineerList.find(e => e.engineer_id === engineerId);
      const ok = await this.confirmAction({
        message: `Remove ${eng ? eng.name : 'engineer'} from this schedule? All dates for this engineer will be unassigned.`,
        header: 'Remove engineer',
        acceptLabel: 'Yes, remove',
        rejectLabel: 'Cancel',
      });
      if (!ok) return;

      // On a saved schedule the page removes the booked days straight away;
      // unsaved days are simply dropped here. Without a listener the old
      // mark-for-removal-until-save behaviour still applies.
      const current = this.value || [];
      const engineerAssignments = current.filter(a => a.engineer_id === engineerId);
      const persistedIds = engineerAssignments.filter(a => a.assignment_id).map(a => a.assignment_id);
      const hasListener = !!(this.$listeners && this.$listeners['remove-engineer']);
      if (persistedIds.length > 0 && hasListener) {
        const keys = new Set(engineerAssignments.map(a => `${a.engineer_id}_${a.scheduled_date}`));
        this.$emit('input', current.filter(a => !keys.has(`${a.engineer_id}_${a.scheduled_date}`)));
        this.$emit('remove-engineer', {
          engineer_id: engineerId,
          engineer_name: eng ? eng.name : engineerId,
          assignment_ids: persistedIds,
        });
        return;
      }
      this.removeEngineer(engineerId);
    },

    fillAll() {
      const updated = [...this.value];
      this.engineerList.forEach(eng => {
        this.dateColumns.forEach(col => {
          if (!this.assignmentMap[`${eng.engineer_id}_${col.epoch}`]) {
            updated.push({
              engineer_id: eng.engineer_id, engineer_name: eng.name, scheduled_date: col.epoch,
              start_time: this.defaultStartTime || null, end_time: this.defaultEndTime || null,
              status: 'Assigned', assignment_id: null,
            });
          }
        });
      });
      this.removedAssignments = [];
      this.$emit('input', updated);
    },

    clearAll() {
      const persisted = this.value.filter(a => a.assignment_id);
      persisted.forEach(a => {
        if (!this.isMarkedForRemoval(a.engineer_id, a.scheduled_date)) {
          this.removedAssignments.push({ engineer_id: a.engineer_id, scheduled_date: a.scheduled_date, assignment_id: a.assignment_id });
        }
      });
      this.$emit('input', persisted);
    },

    async checkAllClashes() {
      if (!this.hasDateRange || this.engineerList.length === 0) {
        this._clashSeq = (this._clashSeq || 0) + 1;
        this.warningMap = {};
        this.isCheckingClashes = false;
        return;
      }
      this._clashSeq = (this._clashSeq || 0) + 1;
      const mySeq = this._clashSeq;
      this.isCheckingClashes = true;
      const assignments = [];
      const map = this.assignmentMap || {};
      this.engineerList.forEach(eng => {
        this.dateColumns.forEach(col => {
          const existing = map[`${eng.engineer_id}_${col.epoch}`];
          const start = existing && existing.start_time ? existing.start_time : (this.defaultStartTime || null);
          const end = existing && existing.end_time ? existing.end_time : (this.defaultEndTime || null);
          assignments.push({
            engineer_id: eng.engineer_id,
            scheduled_date: col.epoch,
            start_time: start,
            end_time: end,
          });
        });
      });
      try {
        const result = await ScheduleService.checkClashes({ assignments, exclude_schedule_id: this.scheduleId || null });
        if (mySeq !== this._clashSeq) return;
        const newMap = {};
        if (result.clashes && result.clashes.length > 0) {
          result.clashes.forEach(clash => {
            const key = `${clash.engineer_id}_${clash.scheduled_date}`;
            if (!newMap[key] || clash.type === 'approved-leave') {
              newMap[key] = {
                type: clash.type,
                message: clash.type === 'approved-leave'
                    ? 'Engineer is on approved leave'
                    : clash.message
                      || `Assigned to another schedule${clash.job_id ? ' (' + clash.job_id + ')' : ''}`,
                schedule_id: clash.schedule_id || null,
                job_id: clash.job_id || null,
                start_time: clash.start_time || null,
                end_time: clash.end_time || null,
              };
            }
          });
        }
        this.warningMap = newMap;
      } catch (err) {
        if (mySeq !== this._clashSeq) return;
        console.error('Error checking clashes:', err);
        this.warningMap = {};
      } finally {
        if (mySeq === this._clashSeq) {
          this.isCheckingClashes = false;
        }
      }
    },

    openAvailabilityPanel(eng) {
      this.availabilityPanel = {
        visible: true,
        engineerId: eng.engineer_id,
        engineerName: eng.name,
        engineerEmail: eng.email || '',
      };
    },

    getRemovedAssignments() {
      return [...this.removedAssignments];
    },

    clearRemoved(assignmentIds) {
      if (!Array.isArray(assignmentIds) || assignmentIds.length === 0) {
        this.removedAssignments = [];
        return;
      }
      const idSet = new Set(assignmentIds);
      this.removedAssignments = this.removedAssignments.filter(r => !idSet.has(r.assignment_id));
    },

    getActivePendingAssignments() {
      return this.value.filter(
        a => !this.isMarkedForRemoval(a.engineer_id, a.scheduled_date),
      );
    },

    // When the job gains days, tick the new days for every engineer already
    // on the board, as pending cells the user can untick before Save. The
    // server still refuses a day the engineer has elsewhere when saving.
    fillEngineersOnAddedDays(newCols, oldCols) {
      if (this.readonly) return;
      if (!Array.isArray(oldCols) || oldCols.length === 0) return; // first load, nothing to compare with
      if (!Array.isArray(newCols) || newCols.length === 0) return;

      const before = new Set(oldCols.map(c => c.epoch));
      const addedDays = newCols.map(c => c.epoch).filter(epoch => !before.has(epoch));
      if (addedDays.length === 0) return;

      const current = this.value || [];
      const taken = new Set(current.map(a => `${a.engineer_id}_${a.scheduled_date}`));
      const added = [];
      for (const eng of this.engineerList) {
        for (const epoch of addedDays) {
          if (taken.has(`${eng.engineer_id}_${epoch}`)) continue;
          added.push({
            engineer_id: eng.engineer_id,
            engineer_name: eng.name,
            engineer_email: eng.email,
            scheduled_date: epoch,
            start_time: this.defaultStartTime || null,
            end_time: this.defaultEndTime || null,
            status: 'Assigned',
            assignment_id: null,
          });
        }
      }
      if (added.length) {
        this.$emit('input', [...current, ...added]);
      }
    },

    dropUnsavedOutsideRange() {
      const startStr = epochToYyyyMmDd(this.startDateEpoch);
      const endStr = epochToYyyyMmDd(this.endDateEpoch);
      if (!startStr || !endStr) return;
      const kept = (this.value || []).filter(a => {
        if (a && a.assignment_id) return true; // never silently drop saved rows
        const dayStr = epochToYyyyMmDd(a && a.scheduled_date);
        return dayStr && isDayInRange(dayStr, startStr, endStr);
      });
      if (kept.length !== (this.value || []).length) {
        this.$emit('input', kept);
      }
    },
  },
};
</script>

<style scoped>
/* ── Board container ── */
.dispatch-board {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

/* ── Scroll wrapper ── */
.dispatch-scroll {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
}

.dispatch-progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  z-index: 40;
  background-image: linear-gradient(90deg, rgba(37,99,235,0) 0%, rgba(37,99,235,0.85) 50%, rgba(37,99,235,0) 100%);
  background-size: 35% 100%;
  background-repeat: no-repeat;
  background-position: -35% 0;
  animation: dispatch-progress-slide 1.1s linear infinite;
  pointer-events: none;
}
@keyframes dispatch-progress-slide {
  0%   { background-position: -35% 0; }
  100% { background-position: 135% 0; }
}

/* ── Table ── */
.dispatch-tbl {
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 100%;
}

/* ── All cells ── */
.dispatch-tbl th,
.dispatch-tbl td {
  border-top: none;
  border-left: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  border-right: none;
  padding: 0;
  text-align: center;
  vertical-align: middle;
}
.dispatch-tbl th:first-child,
.dispatch-tbl td:first-child {
  border-left: 1px solid #e5e7eb;
}
.dispatch-tbl tbody tr:last-child td {
  border-bottom: none;
}

/* ── Header cells ── */
.dispatch-tbl thead th {
  background: #f9fafb;
  border-bottom: 2px solid #d1d5db;
  padding: 8px 4px;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

/* ── Sticky left column ── */
.dt-sticky-left {
  position: sticky !important;
  left: 0;
  z-index: 20 !important;
  isolation: isolate;
  background: #fff;
  text-align: left;
  padding: 8px 12px !important;
  width: 155px;
  min-width: 155px;
  max-width: 155px;
  box-shadow: 8px 0 12px -8px rgba(0, 0, 0, 0.18);
  border-left: 1px solid #d1d5db !important;
}
thead .dt-sticky-left {
  background: #f9fafb !important;
  z-index: 30 !important;
}

/* ── Sticky right column ── */
.dt-sticky-right {
  position: sticky !important;
  right: 0;
  z-index: 20 !important;
  isolation: isolate;
  background: #fff;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  padding: 4px !important;
  box-shadow: -8px 0 12px -8px rgba(0, 0, 0, 0.18);
}
thead .dt-sticky-right {
  background: #f9fafb !important;
  z-index: 30 !important;
}

/* ── Date cells: lower z-index than sticky columns ── */
.dt-date-header,
.dt-date-cell {
  width: 80px;
  min-width: 80px;
  position: relative;
  z-index: 1;
}

/* ── Engineer name cell ── */
.dt-eng-cell {
  padding: 8px 12px !important;
}
.dt-eng-cell.eng-cell-inactive {
  background-color: #f9fafb;
}

/* ── Delete cell ── */
.dt-del-cell {
  text-align: center;
  padding: 4px !important;
}

.assigned-cell {
  border-radius: 4px;
  margin: 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  position: relative;
  padding: 6px 4px 4px 4px;
  overflow: hidden;
}

.corner-badge-left {
  position: absolute;
  top: 3px;
  left: 3px;
  color: #f59e0b;
  z-index: 1;
}

.corner-badge-right {
  position: absolute;
  top: 3px;
  right: 3px;
  z-index: 1;
}

.time-edit-btn {
  color: #6b7280;
  transition: all 0.15s ease;
  line-height: 1;
  padding: 4px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  cursor: pointer;
}
.time-edit-btn:hover {
  color: #15803d;
  background-color: #ffffff;
  border-color: rgba(0, 0, 0, 0.18);
}
.time-edit-btn:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 1px;
}

.cell-assigned { background-color: #dcfce7; color: #15803d; }
.cell-assigned:hover { background-color: #bbf7d0; }
.cell-assigned .time-edit-btn { color: #166534; background-color: rgba(255, 255, 255, 0.5); }
.cell-assigned .time-edit-btn:hover { color: #14532d; background-color: rgba(255, 255, 255, 0.85); }

.cell-inprogress { background-color: #fef3c7; color: #b45309; }

.cell-completed { background-color: #6ee7b7; color: #064e3b; }

.cell-removed { background-color: #fee2e2; color: #dc2626; opacity: 0.6; }

.removed-overlay { display: flex; align-items: center; justify-content: center; }

.empty-cell {
  padding: 8px 4px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  margin: 2px;
  position: relative;
  overflow: hidden;
}
.empty-cell:hover { background-color: #eff6ff; }
.empty-cell:hover svg { color: #3b82f6; }

.warning-badge-empty { color: #f59e0b; }

.header-today {
  background-color: #eff6ff !important;
}

.header-weekend {
  background-color: #fef2f2 !important;
  color: #b91c1c;
}

.cell-bg-weekend {
  background-color: #fef2f2 !important;
}

.cell-bg-leave {
  background: repeating-linear-gradient(45deg, #fee2e2, #fee2e2 3px, #fff5f5 3px, #fff5f5 6px) !important;
}
.cell-bg-clash {
  background-color: #fff7ed !important;
}

</style>
